import { IconClose } from "@/assets/icons/geist/IconClose";
import { Button } from "@/components/flexkit/Button";
import { SHOW_NUDGE_DOWNLOAD_APP, showNudgeDownloadApp } from "@/components/nudge-download-app-utils";
import { Popover, PopoverContent } from "@/components/podkit/popover/Popover";
import { Anchor as PopoverAnchor } from "@radix-ui/react-popover";
import { useDesktop } from "@/hooks/use-desktop";
import { useFlagDesktopDownloadNudgeEnabled } from "@/hooks/use-flag";
import { useDesktopDownloadUrl } from "@/hooks/use-releases-url";
import { useGetPreferenceByKey, useSetPreference } from "@/queries/user-preferences-queries";
import { useCallback, type FC, type PropsWithChildren } from "react";

export const NudgeDownloadApp: FC<PropsWithChildren> = ({ children }) => {
    const { isDesktop } = useDesktop();
    const { value: flagEnabled, loading: flagLoading } = useFlagDesktopDownloadNudgeEnabled();
    const { url: downloadUrl, loading: urlLoading } = useDesktopDownloadUrl();
    const { data: preference, isLoading: prefLoading } = useGetPreferenceByKey(SHOW_NUDGE_DOWNLOAD_APP);
    const { mutate: setPreference } = useSetPreference();

    const shouldShow = showNudgeDownloadApp(isDesktop, flagEnabled, flagLoading);

    const dismissed = preference?.value === "false";
    const isOpen = shouldShow && !prefLoading && !dismissed;

    const handleDismiss = useCallback(() => {
        setPreference({ key: SHOW_NUDGE_DOWNLOAD_APP, value: "false" });
    }, [setPreference]);

    if (!shouldShow) {
        return <>{children}</>;
    }

    return (
        <Popover open={isOpen}>
            <PopoverAnchor asChild>{children}</PopoverAnchor>
            <PopoverContent
                side="right"
                align="start"
                sideOffset={12}
                className="w-72"
                onEscapeKeyDown={handleDismiss}
                onInteractOutside={handleDismiss}
            >
                <div className="flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-content-primary">Try Ona for Mac</p>
                        <button
                            onClick={handleDismiss}
                            className="shrink-0 text-content-secondary hover:text-content-primary"
                            aria-label="Dismiss"
                            data-tracking-id="nudge-download-app-dismiss"
                        >
                            <IconClose size="sm" />
                        </button>
                    </div>
                    <p className="text-sm text-content-secondary">
                        Get a faster, native experience with the Ona desktop app.
                    </p>
                    <Button
                        variant="primary"
                        size="md"
                        asChild
                        disabled={urlLoading}
                        data-tracking-id="nudge-download-app-download"
                    >
                        <a href={downloadUrl} target="_blank" rel="noreferrer">
                            Download for Mac
                        </a>
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
};
